// Port of GroundTeam/Sync/Auth/AuthNonce.swift (WEB_PLAN.md §4.3, §4.4). Nonce for Sign in with Apple and Continue
// with Google: the provider signs the SHA-256 (sent in the authorization request) into the ID token; Supabase gets
// the raw value, hashes it and compares, so a stolen token can't be replayed with another request
// (`skip_nonce_check = false` on the server). Same encodings as iOS so either side can check the other's values:
// raw = base64url(32 random bytes) without padding, hash = lower-case hex SHA-256 of the raw string's UTF-8 bytes.

export interface AuthNonce {
  /** Goes to Supabase (`signInWithIdToken({ nonce })`). */
  raw: string;
  /** Goes to Apple / Google (the `nonce` of the authorization request). */
  sha256: string;
}

/** `count` bytes from the browser's CSPRNG. */
export function randomBytes(count: number): Uint8Array {
  const bytes = new Uint8Array(count);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** Base64url (RFC 4648 §5) without "=" padding. */
export function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Lower-case hex SHA-256 of the UTF-8 bytes of `text`. */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** A fresh nonce pair; use each one for a single sign-in attempt. */
export async function makeNonce(): Promise<AuthNonce> {
  const raw = base64Url(randomBytes(32));
  return { raw, sha256: await sha256Hex(raw) };
}
